import { createSlice, createSelector } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import type {
  RemoteMediaDetectionsState,
  SolidPayload,
  BlackScreenPayload,
} from '../types';

import { deafConnected } from 'features/call/call-deaf/store';
import type { RootState } from 'features/app/store/store';

const initialState: RemoteMediaDetectionsState = {
  lastBlackScreenPayload: null,
  lastSolidScreenPayload: null,
  videoWasAtSomePointDetected: false,
  deafConnectedAt: null,
  lastReportedState: null,
};

const slice = createSlice({
  name: 'remoteMediaDetections',
  initialState,
  reducers: {
    blackFrameDetected(state, action: PayloadAction<BlackScreenPayload>) {
      state.lastBlackScreenPayload = action.payload;
      state.lastReportedState = 'black frame detected';
    },
    solidFrameDetected(state, action: PayloadAction<SolidPayload>) {
      state.lastSolidScreenPayload = action.payload;
      state.lastReportedState = 'solid frame detected';
    },
    videoDetected(state) {
      state.videoWasAtSomePointDetected = true;
      state.lastReportedState = 'detected';
    },
    videoNotDetected(state) {
      state.lastReportedState = 'not detected';
    },

    // Helper for tests or other consumers
    clearAllDetections(state) {
      state.lastBlackScreenPayload = null;
      state.lastSolidScreenPayload = null;
      state.videoWasAtSomePointDetected = false;
      state.deafConnectedAt = null;
      state.lastReportedState = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(deafConnected, (state) => {
      state.deafConnectedAt = Date.now();
    });
  },
});

export const {
  blackFrameDetected,
  solidFrameDetected,
  videoDetected,
  clearAllDetections,
  videoNotDetected,
} = slice.actions;

export const remoteMediaDetectionsReducer = slice.reducer;

// Selectors
export const selectRemoteMediaDetections = createSelector(
  [(state: RootState) => state.remoteMediaDetections],
  (remoteMediaDetections) => remoteMediaDetections ?? initialState
);

export const selectVideoRenderingStatus = createSelector(
  [selectRemoteMediaDetections],
  (detections) => detections.lastReportedState
);
